import { BARCODE_PREFIX } from "../constants/PickingConstant";

/**
 * Picker badges for the pickapp (HVC-54: logging in by scanning your own badge).
 *
 * A badge is only a Code 128 barcode `BADGE-…` with a name next to it; the app keeps no list of
 * valid badges, and every event carries the badge code as `pickerId`. Which name belongs to which
 * code is in the register (`pickers/{code}`), written when badges are printed, so the CMS can show
 * per order and in the picker report who picked. The staff type one name per line; new badges are
 * numbered on above the register, and a line that already names a code (`BADGE-0042 Dick`,
 * `0042 Dick`, or just `0042`) keeps it, so a lost badge is reprinted with the same code.
 *
 * The code rule is the app's (`isValidBadge` in pickapp/src/core/scan.ts): `BADGE-` and then
 * letters, digits, - or _.
 */

const CODE_BODY = /^[A-Za-z0-9_-]{1,40}$/;

export const formatBadgeCode = (number) => `${BARCODE_PREFIX.BADGE}${String(number).padStart(4, "0")}`;

export const isValidBadgeCode = (code) =>
  typeof code === "string" && code.startsWith(BARCODE_PREFIX.BADGE) && CODE_BODY.test(code.slice(BARCODE_PREFIX.BADGE.length));

const explicitCode = (first) => {
  if (first.toUpperCase().startsWith(BARCODE_PREFIX.BADGE)) return `${BARCODE_PREFIX.BADGE}${first.slice(BARCODE_PREFIX.BADGE.length)}`;
  if (/^\d+$/.test(first)) return formatBadgeCode(Number(first));
  return null;
};

/**
 * @param {string} text - one badge per line: "Naam", "BADGE-0042 Naam", "0042 Naam", or only the
 *   code ("BADGE-0042", "0042") to reprint a badge under its registered name
 * @param {Object} [options]
 * @param {number} [options.startNumber] - where numbering of new badges starts
 * @param {string[]} [options.taken] - codes that exist already (the register): never handed out again
 * @returns {{ cards: Array<{code: string, name: string}>, problems: Array<{line: number, text: string, reason: string}> }}
 */
export const parseBadgeLines = (text, { startNumber = 1, taken = [] } = {}) => {
  const parsed = (text || "")
    .split(/\r?\n/)
    .map((raw, index) => ({ line: raw.trim(), number: index + 1 }))
    .filter(({ line }) => !!line)
    .map(({ line, number }) => {
      const [first, ...rest] = line.split(/\s+/);
      const code = explicitCode(first);
      return { line, number, code, name: code === null ? line : rest.join(" ") };
    });

  // A number given anywhere in the text, or already in the register, is never given to a new badge
  const reserved = new Set([...(taken || []), ...parsed.map((entry) => entry.code).filter((code) => !!code && isValidBadgeCode(code))]);
  const cards = [];
  const problems = [];
  const used = new Set();
  let next = Number.isInteger(startNumber) && startNumber > 0 ? startNumber : 1;

  parsed.forEach(({ line, number, code: given, name }) => {
    let code = given;
    if (code === null) {
      while (reserved.has(formatBadgeCode(next))) next += 1;
      code = formatBadgeCode(next);
      reserved.add(code);
      next += 1;
    }
    if (!isValidBadgeCode(code)) {
      problems.push({ line: number, text: line, reason: "invalid-code" });
      return;
    }
    if (used.has(code)) {
      problems.push({ line: number, text: line, reason: "duplicate-code" });
      return;
    }
    used.add(code);
    cards.push({ code, name: name.trim() });
  });
  return { cards, problems };
};

/** The number of a numbered badge (BADGE-0042 is 42); null for a code like BADGE-DICK. */
export const badgeNumber = (code) => {
  const match = /^BADGE-(\d{1,9})$/.exec(code || "");
  return match ? Number(match[1]) : null;
};

/** The first number above every numbered badge in the register, so a new badge never reuses a code. */
export const nextBadgeNumber = (pickers) =>
  (pickers || []).reduce((highest, picker) => Math.max(highest, badgeNumber(picker && picker.code) || 0), 0) + 1;

const sameName = (a, b) => (a || "").trim().toLocaleLowerCase("nl") === (b || "").trim().toLocaleLowerCase("nl");

export const BADGE_PLAN = { NEW: "new", REPRINT: "reprint", REACTIVATE: "reactivate" };

/**
 * What printing these cards does to the register (`pickers/{code}`, data/hooks/usePickers).
 *
 *  - a new code with a name is added;
 *  - a known code is a reprint: it keeps its registered name - a line with only the code prints
 *    that name - and a badge that was switched off is switched on again;
 *  - a known code with another name is refused, and so is a new code without a name. A badge
 *    belongs to one person: giving its code to someone else would hand them the first person's
 *    history. A new picker gets a new badge; a typo is fixed on the Pickers page.
 *
 * @param {Array<{code, name}>} cards - from parseBadgeLines
 * @param {Array<{code, name, active}>} pickers - the register
 * @returns {{ ok: boolean, cards: Array<{code, name, kind}>, problems: Array<{code, name, reason, registeredName?}> }}
 */
export const planBadgeRegistration = (cards, pickers) => {
  const register = new Map((pickers || []).map((picker) => [picker.code, picker]));
  const planned = [];
  const problems = [];
  (cards || []).forEach((card) => {
    const known = register.get(card.code);
    if (!known) {
      if (!card.name) problems.push({ code: card.code, name: "", reason: "no-name" });
      else planned.push({ code: card.code, name: card.name, kind: BADGE_PLAN.NEW });
      return;
    }
    if (card.name && !sameName(card.name, known.name)) {
      problems.push({ code: card.code, name: card.name, reason: "name-mismatch", registeredName: known.name || "" });
      return;
    }
    planned.push({ code: card.code, name: known.name || card.name, kind: known.active === false ? BADGE_PLAN.REACTIVATE : BADGE_PLAN.REPRINT });
  });
  return { ok: problems.length === 0 && planned.length > 0, cards: planned, problems };
};
